import React, { useEffect, useMemo, useState } from 'react';
import { Field, INP, Select } from '../../../reusable/formRecords/formFieldPrimitives';
import { FIELD_SOURCE_OPTIONS, FIELD_SELECTOR_TEMPLATES, createFieldBlueprint } from './formRegistryData';

const DRAWER_BASE = 'fixed right-0 top-0 z-[110] flex h-full w-full max-w-[460px] flex-col bg-white shadow-2xl transition-transform';
const NESTED_DRAWER_BASE = 'fixed right-[460px] top-0 z-[120] flex h-full w-full max-w-[420px] flex-col bg-white shadow-2xl transition-transform';

const DEFAULT_VALIDATION = {
  min: '',
  max: '',
  minLength: '',
  maxLength: '',
  pattern: '',
  message: '',
};

function normalizeOption(option, index = 0) {
  if (option === null || option === undefined) {
    return { value: `option-${index + 1}`, label: `Option ${index + 1}` };
  }

  if (typeof option === 'string' || typeof option === 'number') {
    const value = String(option).trim();
    return value ? { value, label: value } : { value: `option-${index + 1}`, label: `Option ${index + 1}` };
  }

  const value = String(option.value ?? option.id ?? option.code ?? option.label ?? option.name ?? `option-${index + 1}`).trim();
  const label = String(option.label ?? option.name ?? option.title ?? value).trim() || value;

  return {
    value,
    label,
  };
}

function normalizeValidation(validation) {
  return { ...DEFAULT_VALIDATION, ...(validation ?? {}) };
}

function normalizeDraftField(field) {
  return {
    ...field,
    label: field?.label ?? '',
    hint: field?.hint ?? '',
    placeholder: field?.placeholder ?? '',
    required: Boolean(field?.required),
    layout: field?.layout ?? 'half-width',
    source: field?.source ?? '',
    options: Array.isArray(field?.options) ? field.options.map(normalizeOption) : [],
    validation: normalizeValidation(field?.validation),
    allowOther: Boolean(field?.allowOther),
    defaultValue: field?.defaultValue ?? field?.value ?? '',
    otherFieldKey: field?.otherFieldKey ?? '',
  };
}

function DrawerHeader({ title, subtitle, onClose }) {
  return (
    <header className="flex items-start justify-between gap-3 rounded-t-lg bg-green-500 px-5 py-4">
      <div>
        <h3 className="text-base font-bold text-white">{title}</h3>
        {subtitle && <p className="mt-1 text-xs text-green-50/90">{subtitle}</p>}
      </div>
      <button
        type="button"
        onClick={onClose}
        className="text-white/90 hover:text-white"
        aria-label="Close drawer"
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </header>
  );
}

function OptionRow({ option, index, onChange, onRemove }) {
  return (
    <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
      <input
        value={option.label ?? ''}
        onChange={(event) => onChange(index, 'label', event.target.value)}
        placeholder={`Option ${index + 1} label`}
        className={INP}
      />
      <input
        value={option.value ?? ''}
        onChange={(event) => onChange(index, 'value', event.target.value)}
        placeholder={`option-${index + 1}`}
        className={INP}
      />
      <button
        type="button"
        onClick={() => onRemove(index)}
        className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-400 transition hover:border-red-200 hover:text-red-600"
        aria-label={`Remove option ${index + 1}`}
      >
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6l-12 12" />
        </svg>
      </button>
    </div>
  );
}

function DropdownOptionsDrawer({ open, draft, onClose, onSave, onChange }) {
  const [options, setOptions] = useState([]);
  const [source, setSource] = useState('');
  const [placeholder, setPlaceholder] = useState('');
  const [allowOther, setAllowOther] = useState(false);

  useEffect(() => {
    if (!open) return;
    setOptions((draft?.options ?? []).map(normalizeOption));
    setSource(String(draft?.source ?? '').trim());
    setPlaceholder(String(draft?.placeholder ?? '').trim());
    setAllowOther(Boolean(draft?.allowOther));
  }, [draft, open]);

  const updateOption = (index, key, value) => {
    setOptions((current) => current.map((option, currentIndex) => (
      currentIndex === index ? { ...option, [key]: value } : option
    )));
  };

  const addOption = () => {
    setOptions((current) => [...current, { value: `option-${current.length + 1}`, label: `Option ${current.length + 1}` }]);
  };

  const removeOption = (index) => {
    setOptions((current) => current.filter((_, currentIndex) => currentIndex !== index));
  };

  const commit = () => {
    onChange({
      ...draft,
      options: options.map(normalizeOption),
      source,
      placeholder,
      allowOther,
    });
    onSave({
      ...draft,
      options: options.map(normalizeOption),
      source,
      placeholder,
      allowOther,
    });
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[119] bg-slate-950/40"
      />
      <aside className={`${NESTED_DRAWER_BASE} ${open ? 'translate-x-0' : 'translate-x-full'}`}>
        <DrawerHeader
          title="Dropdown Options"
          subtitle="Configure manual options and the fallback data source."
          onClose={onClose}
        />

        <div className="flex-1 space-y-5 overflow-auto px-5 py-5">
          <Field label="Source" hint="Optional remote source used when available.">
            <Select
              value={source}
              onChange={(event) => setSource(event.target.value)}
              options={FIELD_SOURCE_OPTIONS}
              placeholder="Manual entry"
            />
          </Field>

          <Field label="Placeholder">
            <input
              value={placeholder}
              onChange={(event) => setPlaceholder(event.target.value)}
              placeholder="Select..."
              className={INP}
            />
          </Field>

          <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm font-medium text-gray-700">
            <input
              type="checkbox"
              checked={allowOther}
              onChange={(event) => setAllowOther(event.target.checked)}
              className="accent-green-600"
            />
            Allow "Other" option
          </label>

          <div className="space-y-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h4 className="text-sm font-bold text-gray-900">Options</h4>
                <p className="mt-1 text-xs text-gray-500">Edit the labels and values shown in the dropdown.</p>
              </div>
              <button
                type="button"
                onClick={addOption}
                className="rounded-full bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-700"
              >
                + Add Option
              </button>
            </div>

            <div className="space-y-2">
              {options.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-200 px-4 py-5 text-center text-xs text-gray-400">
                  No options yet. Add a few defaults before saving.
                </div>
              ) : (
                options.map((option, index) => (
                  <OptionRow
                    key={`${option.value ?? index}-${index}`}
                    option={option}
                    index={index}
                    onChange={updateOption}
                    onRemove={removeOption}
                  />
                ))
              )}
            </div>
          </div>

        </div>

        <footer className="border-t border-gray-100 px-5 py-4">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="h-11 flex-1 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50"
            >
              Back
            </button>
            <button
              type="button"
              onClick={commit}
              className="h-11 flex-1 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700"
            >
              Save Options
            </button>
          </div>
        </footer>
      </aside>
    </>
  );
}

function FieldTypePickerDrawer({ open, currentType, onClose, onSelect }) {
  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[119] bg-slate-950/40"
      />
      <aside className={`${NESTED_DRAWER_BASE} translate-x-0`}>
        <DrawerHeader
          title="Change Field Type"
          subtitle="Switch this field to a different input type from the Form Field Selector. Type-specific settings will reset."
          onClose={onClose}
        />

        <div className="flex-1 space-y-2 overflow-auto px-5 py-5">
          {FIELD_SELECTOR_TEMPLATES.map((template) => {
            const active = template.type === currentType;
            return (
              <button
                key={template.type}
                type="button"
                onClick={() => !active && onSelect(template.type)}
                disabled={active}
                className={`flex w-full items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left transition ${
                  active
                    ? 'cursor-default border-green-300 bg-green-50'
                    : 'border-gray-200 bg-white hover:border-green-200 hover:bg-green-50/60'
                }`}
              >
                <span>
                  <span className="block text-sm font-semibold text-gray-900">{template.label}</span>
                  <span className="mt-0.5 block text-xs text-gray-500">{template.description}</span>
                </span>
                {active && (
                  <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-green-700">Current</span>
                )}
              </button>
            );
          })}
        </div>

        <footer className="border-t border-gray-100 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="h-11 w-full rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50"
          >
            Back
          </button>
        </footer>
      </aside>
    </>
  );
}

export function FieldSettingsDrawer({ open, field, onClose, onBack, onSave }) {
  const [draft, setDraft] = useState(() => normalizeDraftField(field));
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [typePickerOpen, setTypePickerOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(normalizeDraftField(field));
    setOptionsOpen(false);
    setTypePickerOpen(false);
  }, [field, open]);

  const updateDraft = (key, value) => {
    setDraft((current) => ({ ...current, [key]: value }));
  };

  const handleTypeChange = (newType) => {
    const blueprint = createFieldBlueprint(newType);
    setDraft((current) => normalizeDraftField({
      ...blueprint,
      id: current.id,
      label: current.label || blueprint.label,
      hint: current.hint || blueprint.hint,
      required: current.required,
      layout: current.layout || blueprint.layout,
    }));
    setTypePickerOpen(false);
  };

  const commit = (nextDraft = draft) => {
    onSave({
      ...nextDraft,
      options: Array.isArray(nextDraft.options) ? nextDraft.options.map(normalizeOption) : [],
      validation: normalizeValidation(nextDraft.validation),
    });
  };

  const fieldTypeLabel = useMemo(() => String(draft.type ?? '').toUpperCase(), [draft.type]);

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[108] bg-slate-950/40"
      />
      <aside className={`${DRAWER_BASE} ${open ? 'translate-x-0' : 'translate-x-full'}`}>
        <DrawerHeader
          title="Field Settings"
          subtitle={fieldTypeLabel ? `${fieldTypeLabel} field configuration` : 'Configure the field properties.'}
          onClose={onClose}
        />

        <div className="flex-1 space-y-5 overflow-auto px-5 py-5">
          <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="inline-flex rounded-full bg-green-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-green-700">
                {fieldTypeLabel || 'FIELD'}
              </div>
              <button
                type="button"
                onClick={() => setTypePickerOpen(true)}
                className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 hover:border-green-200 hover:text-green-700"
              >
                Change Type
              </button>
            </div>

            <div className="space-y-4">
              <Field label="Label" required>
                <input
                  value={draft.label}
                  onChange={(event) => updateDraft('label', event.target.value)}
                  placeholder="Field label"
                  className={INP}
                />
              </Field>

              <Field label="Hint">
                <input
                  value={draft.hint}
                  onChange={(event) => updateDraft('hint', event.target.value)}
                  placeholder="Helper text shown below the field"
                  className={INP}
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Required">
                  <label className="flex h-11 items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={draft.required}
                      onChange={(event) => updateDraft('required', event.target.checked)}
                      className="accent-green-600"
                    />
                    Mark as required
                  </label>
                </Field>

                <Field label="Layout">
                  <Select
                    value={draft.layout}
                    onChange={(event) => updateDraft('layout', event.target.value)}
                    options={[
                      { value: 'half-width', label: 'Half width' },
                      { value: 'full-width', label: 'Full width' },
                    ]}
                    placeholder="Select layout"
                  />
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Placeholder">
                  <input
                    value={draft.placeholder}
                    onChange={(event) => updateDraft('placeholder', event.target.value)}
                    placeholder="Enter placeholder"
                    className={INP}
                  />
                </Field>
                <Field label="Default Value">
                  <input
                    value={draft.defaultValue}
                    onChange={(event) => updateDraft('defaultValue', event.target.value)}
                    placeholder="Optional default"
                    className={INP}
                  />
                </Field>
              </div>
            </div>
          </div>

          {(draft.type === 'dropdown' || draft.type === 'radio' || draft.type === 'checkbox') && (
            <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h4 className="text-sm font-bold text-gray-900">Choice List</h4>
                  <p className="mt-1 text-xs text-gray-500">Configure the value list for this choice field.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setOptionsOpen(true)}
                  className="rounded-full border border-green-200 bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-700 hover:bg-green-100"
                >
                  Edit Options
                </button>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3">
                <Field label="Source">
                  <Select
                    value={draft.source}
                    onChange={(event) => updateDraft('source', event.target.value)}
                    options={FIELD_SOURCE_OPTIONS}
                    placeholder="Manual entry"
                  />
                </Field>

                <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={draft.allowOther}
                    onChange={(event) => updateDraft('allowOther', event.target.checked)}
                    className="accent-green-600"
                  />
                  Allow "Other" option
                </label>
              </div>
            </div>
          )}

        </div>

        <footer className="border-t border-gray-100 px-5 py-4">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onBack || onClose}
              className="h-11 flex-1 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50"
            >
              {onBack ? 'Back' : 'Cancel'}
            </button>
            <button
              type="button"
              onClick={() => commit(draft)}
              className="h-11 flex-1 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700"
            >
              Save Field
            </button>
          </div>
        </footer>
      </aside>

      <DropdownOptionsDrawer
        open={optionsOpen}
        draft={draft}
        onClose={() => setOptionsOpen(false)}
        onSave={(nextDraft) => {
          setDraft((current) => ({
            ...current,
            ...nextDraft,
          }));
          setOptionsOpen(false);
        }}
        onChange={(nextDraft) => {
          setDraft((current) => ({
            ...current,
            ...nextDraft,
          }));
        }}
      />

      <FieldTypePickerDrawer
        open={typePickerOpen}
        currentType={draft.type}
        onClose={() => setTypePickerOpen(false)}
        onSelect={handleTypeChange}
      />
    </>
  );
}
